import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    akvToResource,
    akvToModel,
    parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { AzureFileStorageLinkedService } from 'app/model/linkedservice/azure-file-storage.linkedservice';
import { AdapterContext, AzureKeyVault, azureStorageAuthTypes, Credential, ConnectionAuthKind, isString, ITridentResource, SharedModel, ReferenceAdapter } from 'shell-models-lib';
import { IStringMap } from 'app/shared/utils/expression.util';
import { getValue } from 'app/extensibility/utils/property-bag.util';
import { Constants } from 'app/shared/shared-constants';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureFileStorageLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                host: '*',
                userId: '*',
                password: 'secret',
                encryptedCredential: '*',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzureFileStorageLinkedService) => {
                    if (ls && !ls.fullyByJsonEditor && ls.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication && !ls.enableAccountAKV) {
                        if (ls.accountKey instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                accountKey: 'secret',
                sasUri: 'secret',
                sasToken: 'secret',
                fileShare: '*',
                snapshot: '*',
                serviceEndpoint: '*',
                credential: '*',
            },
            parameters: '*'
        }
    }
})
export class AzureFileStorageLinkedServiceAdapter extends LinkedServiceAdapter<AzureFileStorageLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => AzureFileStorageLinkedService {
        return AzureFileStorageLinkedService;
    }

    protected model(): IModel {
        return AzureFileStorageLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (
        tridentResource: ITridentResource
    ): Partial<Model.AzureFileStorageLinkedServiceTypeProperties> => {
        const credentialType = tridentResource.credentialDetails.credentialType;
        switch (credentialType) {
            case ConnectionAuthKind.Key:
                const datasourceReference: Record<'kind' | 'path', string> = JSON.parse(
                    tridentResource.datasourceReference
                );
                const response: Partial<Model.AzureFileStorageLinkedServiceTypeProperties> = {};
                try {
                    const json = JSON.parse(datasourceReference.path);
                    // expected format: https://<url>/<fileShare>
                    // after split: ['https:', '', <url>, <fileShare>]
                    const paths = json?.shareUrl?.split('/').filter((s: string) => s.length > 0);
                    if (paths?.[1]) {
                        // <url> format: <AccountName>.file.<EndpointSuffix>
                        const uris = paths[1].split('.');
                        const suffix = uris.slice(2).join('.');
                        response.connectionString = `DefaultEndpointsProtocol=https;AccountName=${uris[0]};EndpointSuffix=${suffix}`;
                    }
                    if (paths?.[2]) {
                        response.fileShare = paths[2];
                    }
                } catch (e) {
                    this.ensureLogger().error('AzureFileStorageLinkedServiceAdapter', e);
                }
                return response;
            default:
                return undefined;
        }
    };

    protected async propertiesToResourceAsync(linkedService: AzureFileStorageLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureFileStorageLinkedService;

        properties.type = Metamodel.modelFor(AzureFileStorageLinkedService).name;

        if (linkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication) {
            if (linkedService.enableSasUriAKV && linkedService.referedSasUriAKV instanceof AzureKeyVault) {
                properties.typeProperties = {
                    sasUri: akvToResource(linkedService.referedSasUriAKV, this.context)
                };
            } else {
                let sasUri = this.valueToResource(linkedService, AzureFileStorageLinkedService.SAS_URI);
                if (linkedService.sasToken && isString(linkedService.sasToken)) {
                    let sasToken = linkedService.sasToken as string;
                    if (sasUri.endsWith('?')) {
                        sasUri = sasUri.substr(0, sasUri.length - 1);
                    }
                    if (sasToken.startsWith('?')) {
                        sasToken = sasToken.substr(1);
                    }
                    properties.typeProperties = {
                        sasUri: sasUri + '?' + sasToken
                    };
                } else {
                    properties.typeProperties = {
                        sasUri,
                        sasToken: parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureFileStorageLinkedService.SAS_TOKEN, this.context) as SharedModel.AzureKeyVaultSecretReference
                    };
                }
            }
            // Should the next line be put in the above 'else' case?
            properties.typeProperties.fileShare = this.valueToResource(linkedService, AzureFileStorageLinkedService.FILE_SHARE);
            properties.typeProperties.snapshot = linkedService.snapshot;
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication) {
            if (!linkedService.enableAccountAKV) {
                // Why not use a real map?
                const propertiesBag: IStringMap<string | Model.SecretBase | undefined> = {};
                propertiesBag['DefaultEndpointsProtocol'] = 'https';
                propertiesBag['AccountName'] = this.valueToResource(linkedService, AzureFileStorageLinkedService.ACCOUNT_NAME);
                if (linkedService.primaryEndpoint) {
                    // can't check `isPartitionedDns` property as azure subscription doesn't have this checkbox
                    const primaryEndpoint = this.valueToResource(linkedService, AzureFileStorageLinkedService.PRIMARY_ENDPOINT);
                    if (primaryEndpoint) {
                        propertiesBag['FileEndpoint'] = primaryEndpoint;
                        // if user changed existing LS from old to new, so endpointSuffix should be removed
                        // as they should be mutually exclusive
                        delete linkedService.endpointSuffix;
                    }
                } else {
                    const endpointSuffix = this.valueToResource(linkedService, AzureFileStorageLinkedService.ENDPOINT_SUFFIX);
                    if (endpointSuffix) {
                        propertiesBag['EndpointSuffix'] = endpointSuffix;
                        // if user changed existing LS from new to old, so primaryEndpoint should be removed
                        // as they should be mutually exclusive
                        delete linkedService.primaryEndpoint;
                    }
                }
                if (linkedService.accountKey && !(linkedService.accountKey instanceof AzureKeyVault)) {
                    propertiesBag['AccountKey'] = linkedService.accountKey;
                } else if (!linkedService.accountKey) {
                    propertiesBag['AccountKey'] = parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureFileStorageLinkedService.ACCOUNT_KEY, this.context);
                }

                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        if (pair.value != null) {
                            propertiesBag[pair.key] = pair.value;
                        }
                    });
                }

                let connectionString = '';
                Object.keys(propertiesBag).forEach((key) => {
                    const value = getValue(propertiesBag, key);
                    if (value != null) {
                        connectionString += `${key}=${value};`;
                    }
                });
                properties.typeProperties = {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    connectionString: linkedService.accountName === Constants.maskedCredential
                    && linkedService.accountKey === Constants.maskedCredential ?
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        ({
                            type: 'SecureString',
                            value: Constants.maskedCredential
                        } as Model.SecureString) : this._connectionStringToResource(connectionString),
                    accountKey: linkedService.accountKey instanceof AzureKeyVault ? akvToResource(linkedService.accountKey, this.context) :
                        !linkedService.accountKey ? propertiesBag['AccountKey'] as Model.AzureKeyVaultSecretReference : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAccountAKV, this.context)
                };
            }
            properties.typeProperties.fileShare = this.valueToResource(linkedService, AzureFileStorageLinkedService.FILE_SHARE);
            properties.typeProperties.snapshot = linkedService.snapshot;
        } else if (linkedService.authenticationMethod === AuthenticationType.Basic) {
            properties.typeProperties = {
                host: this.valueToResource(linkedService, AzureFileStorageLinkedService.HOST),
                userId: this.valueToResource(linkedService, AzureFileStorageLinkedService.USER_NAME),
                password: parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureFileStorageLinkedService.PASSWORD, this.context)
            };
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication) {
            properties.typeProperties = {
                serviceEndpoint: this.valueToResource(linkedService, AzureFileStorageLinkedService.SERVICE_ENDPOINT),
                fileShare: this.valueToResource(linkedService, AzureFileStorageLinkedService.FILE_SHARE),
                snapshot: linkedService.snapshot
            };
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.UserAssignedManagedIdentity) {
            properties.typeProperties = {
                serviceEndpoint: this.valueToResource(linkedService, AzureFileStorageLinkedService.SERVICE_ENDPOINT),
                credential: linkedService.credential
                    ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                    : undefined,
                fileShare: this.valueToResource(linkedService, AzureFileStorageLinkedService.FILE_SHARE),
                snapshot: linkedService.snapshot
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureFileStorageLinkedService): Promise<AzureFileStorageLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureFileStorageLinkedService;

        if (properties.typeProperties.sasUri) {
            linkedService.authenticationMethod = azureStorageAuthTypes.SasUriAuthentication;
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.FILE_SHARE, properties.typeProperties.fileShare);
            linkedService.snapshot = properties.typeProperties.snapshot;
            if (isString(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.SAS_URI, properties.typeProperties.sasUri);
                parameterizableSecretBaseToModel(linkedService, AzureFileStorageLinkedService.SAS_TOKEN, properties.typeProperties.sasToken, this.context);
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                linkedService.sasUri = Constants.maskedCredential;
                parameterizableSecretBaseToModel(linkedService, AzureFileStorageLinkedService.SAS_TOKEN, properties.typeProperties.sasToken, this.context);
            } else if ((properties.typeProperties.sasUri as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.enableSasUriAKV = true;
                linkedService.referedSasUriAKV = akvToModel(properties.typeProperties.sasUri as Model.AzureKeyVaultSecretReference, this.context);
            }
        } else if (isString(properties.typeProperties.connectionString)) {
            linkedService.authenticationMethod = azureStorageAuthTypes.AccountKeyAuthentication;
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.FILE_SHARE, properties.typeProperties.fileShare);
            linkedService.snapshot = properties.typeProperties.snapshot;
            const keyValPairs = (properties.typeProperties.connectionString as string).split(';');
            keyValPairs.forEach((item) => {
                // assume key doesn't contain '='
                const sepIndex = item.indexOf('=');
                if (sepIndex >= 0) {
                    const key = item.substr(0, sepIndex);
                    const val = item.substr(sepIndex + 1);
                    switch (key) {
                        case 'AccountName':
                            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.ACCOUNT_NAME, val);
                            break;
                        case 'EndpointSuffix':
                            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.ENDPOINT_SUFFIX, val);
                            break;
                        case 'FileEndpoint':
                                ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.PRIMARY_ENDPOINT, val);
                            break;
                        case 'DefaultEndpointsProtocol':
                            if (val !== 'https') {
                                linkedService.additionalFields.push({ key, value: val });
                            }
                            break;
                        default:
                            linkedService.additionalFields.push({ key, value: val });
                    }
                }
            });
                linkedService.isPartitionedDns = !!linkedService.primaryEndpoint;
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                parameterizableSecretBaseToModel(linkedService, AzureFileStorageLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.authenticationMethod = azureStorageAuthTypes.AccountKeyAuthentication;
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.FILE_SHARE, properties.typeProperties.fileShare);
            linkedService.snapshot = properties.typeProperties.snapshot;
            linkedService.accountName = Constants.maskedCredential;
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                parameterizableSecretBaseToModel(linkedService, AzureFileStorageLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
            }
        } else if (properties.typeProperties.connectionString && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.authenticationMethod = azureStorageAuthTypes.AccountKeyAuthentication;
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.FILE_SHARE, properties.typeProperties.fileShare);
            linkedService.snapshot = properties.typeProperties.snapshot;
            linkedService.referedAccountAKV = akvToModel(properties.typeProperties.connectionString as Model.SecretBase, this.context);
            linkedService.enableAccountAKV = true;
        } else if (properties.typeProperties.serviceEndpoint) {
            ValueAdapter.toModel(
                linkedService,
                AzureFileStorageLinkedService.SERVICE_ENDPOINT,
                properties.typeProperties.serviceEndpoint
            );
            if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential, this.context
                );
                linkedService.authenticationMethod = azureStorageAuthTypes.UserAssignedManagedIdentity;
            } else {
                linkedService.authenticationMethod = azureStorageAuthTypes.MSIAuthentication;
            }
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.FILE_SHARE, properties.typeProperties.fileShare);
            linkedService.snapshot = properties.typeProperties.snapshot;
        } else {
            linkedService.authenticationMethod = AuthenticationType.Basic;
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.HOST, properties.typeProperties.host);
            ValueAdapter.toModel(linkedService, AzureFileStorageLinkedService.USER_NAME, properties.typeProperties.userId);
            parameterizableSecretBaseToModel(linkedService, AzureFileStorageLinkedService.PASSWORD, properties.typeProperties.password, this.context);
        }

        return linkedService;
    }
}
